import React, { useState, useCallback } from "react";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import type { DevToolsPlugin } from "../../types";

interface AutofillPluginProps {
  journeyId?: string;
  site?: string;
  answerSets?: Record<string, [string, string][]>;
  fillJourneyAnswersPath?: string;
  fillCustomJourneyAnswersPath?: string;
}

function csrfToken(): string {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") || ""
  );
}

function AutofillPanel({
  journeyId,
  site,
  answerSets = {},
  fillJourneyAnswersPath = "/fill_journey_answers",
  fillCustomJourneyAnswersPath = "/fill_custom_journey_answers",
}: AutofillPluginProps) {
  const [customJson, setCustomJson] = useState("");
  const [selectedPreset, setSelectedPreset] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitForm = useCallback(
    (endpoint: string, key: string, value: string) => {
      if (!journeyId) return;
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append("journey_id", journeyId);
      if (site) formData.append("site", site);
      formData.append(key, value);

      fetch(endpoint, {
        method: "POST",
        body: formData,
        headers: { "X-CSRF-Token": csrfToken() },
      })
        .then((response) => {
          if (response.redirected) {
            window.location.href = response.url;
          }
        })
        .catch((error) => console.error("Autofill error:", error))
        .finally(() => setIsSubmitting(false));
    },
    [journeyId, site],
  );

  if (!journeyId) {
    return (
      <div className="dt-status-banner dt-status-banner--warning">
        <div className="dt-status-banner__title">No journey active</div>
        <div className="dt-status-banner__text">
          Navigate to a journey page to use autofill
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="dt-section-label">Preset Answer Set</div>
      <div className="dt-form-field">
        <select
          className="dt-form-select"
          value={selectedPreset}
          onChange={(e) => setSelectedPreset(e.target.value)}
        >
          <option value="">Select an answer set...</option>
          {Object.entries(answerSets).map(([groupLabel, options]) => (
            <optgroup key={groupLabel} label={groupLabel}>
              {options.map(([label, value]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      <button
        className="dt-btn dt-btn--primary"
        onClick={() =>
          submitForm(fillJourneyAnswersPath, "answer_set", selectedPreset)
        }
        disabled={!selectedPreset || isSubmitting}
        type="button"
      >
        {isSubmitting ? "Submitting..." : "Submit Answer Set"}
      </button>

      <hr className="dt-autofill__divider" />

      <div className="dt-section-label">Custom JSON</div>
      <div className="dt-form-field">
        <textarea
          className="dt-form-textarea"
          placeholder="Enter answer set in JSON format..."
          value={customJson}
          onChange={(e) => setCustomJson(e.target.value)}
        />
      </div>
      <button
        className="dt-btn dt-btn--primary"
        onClick={() =>
          submitForm(
            fillCustomJourneyAnswersPath,
            "custom_answer_set",
            customJson,
          )
        }
        disabled={!customJson || isSubmitting}
        type="button"
      >
        {isSubmitting ? "Submitting..." : "Submit Custom Answers"}
      </button>
    </>
  );
}

export function autofillPlugin(props: AutofillPluginProps): DevToolsPlugin {
  return {
    id: "autofill",
    name: "Autofill",
    icon: faPenToSquare,
    render: () => <AutofillPanel {...props} />,
  };
}
